(() => {
  'use strict';

  const stores = new Map();
  const elementFor = (value) => typeof value === 'string' ? document.querySelector(value) : value;
  const storeFor = (name) => {
    if (!stores.has(name)) stores.set(name, new WeakMap());
    return stores.get(name);
  };
  const emit = (element, name, detail = {}) => element.dispatchEvent(new CustomEvent(name, {
    bubbles: true,
    cancelable: true,
    detail,
  }));
  const targetFor = (trigger) => {
    const selector = trigger?.getAttribute('data-bs-target') || trigger?.getAttribute('href');
    if (!selector || !selector.startsWith('#')) return null;
    try { return document.querySelector(selector); } catch { return null; }
  };
  const focusable = (root) => root.querySelector('button:not([disabled]), a[href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])');
  const openOverlays = new Set();
  let savedOverflow = '';

  const lockScroll = (element) => {
    if (!openOverlays.size) {
      savedOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
    }
    openOverlays.add(element);
  };
  const unlockScroll = (element) => {
    openOverlays.delete(element);
    if (!openOverlays.size) document.body.style.overflow = savedOverflow;
  };
  const transitionDurationMs = (element) => {
    if (!element) return 0;
    const style = getComputedStyle(element);
    const durations = style.transitionDuration.split(',').map((value) => value.trim().endsWith('ms') ? parseFloat(value) : parseFloat(value) * 1000);
    const delays = style.transitionDelay.split(',').map((value) => value.trim().endsWith('ms') ? parseFloat(value) : parseFloat(value) * 1000);
    return Math.max(0, ...durations.map((duration, index) => duration + (delays[index] || delays[0] || 0)));
  };
  const hydrateDeferredImages = (root) => {
    root.querySelectorAll('img[data-v4-src]').forEach((image) => {
      if (image.dataset.v4Srcset) image.srcset = image.dataset.v4Srcset;
      image.src = image.dataset.v4Src;
      delete image.dataset.v4Src;
      delete image.dataset.v4Srcset;
    });
  };

  class Component {
    constructor(element) {
      this._element = elementFor(element);
      if (!this._element) throw new Error('V4 UI component target not found');
      storeFor(this.constructor.NAME).set(this._element, this);
    }
    dispose() { storeFor(this.constructor.NAME).delete(this._element); }
    static getInstance(element) { return storeFor(this.NAME).get(elementFor(element)) || null; }
    static getOrCreateInstance(element, options) { return this.getInstance(element) || new this(element, options); }
  }

  class Offcanvas extends Component {
    static NAME = 'offcanvas';
    show(relatedTarget) {
      if (this._element.classList.contains('show')) return;
      if (!emit(this._element, 'show.bs.offcanvas', { relatedTarget })) return;
      if (this._element.parentElement !== document.body) document.body.appendChild(this._element);
      hydrateDeferredImages(this._element);
      document.querySelectorAll('.offcanvas.show').forEach((element) => {
        if (element !== this._element) Offcanvas.getOrCreateInstance(element).hide();
      });
      this._relatedTarget = relatedTarget || document.activeElement;
      this._element.classList.remove('hiding');
      this._element.removeAttribute('aria-hidden');
      this._element.classList.add('show');
      this._element.setAttribute('aria-modal', 'true');
      this._element.setAttribute('role', 'dialog');
      const backdrop = document.createElement('div');
      backdrop.className = 'offcanvas-backdrop fade show';
      backdrop.dataset.v4Backdrop = this._element.id;
      backdrop.addEventListener('click', () => {
        if (this._element.dataset.bsBackdrop !== 'static') this.hide();
      });
      document.body.appendChild(backdrop);
      lockScroll(this._element);
      focusable(this._element)?.focus({ preventScroll: true });
      emit(this._element, 'shown.bs.offcanvas', { relatedTarget });
    }
    hide() {
      if (!this._element.classList.contains('show')) return;
      if (!emit(this._element, 'hide.bs.offcanvas')) return;
      const backdrop = document.querySelector(`[data-v4-backdrop="${CSS.escape(this._element.id)}"]`);
      const duration = Math.max(transitionDurationMs(this._element), 1);
      if (backdrop) backdrop.style.transitionDuration = `${duration}ms`;
      this._element.classList.add('hiding');
      this._element.classList.remove('show');
      backdrop?.classList.remove('show');
      this._element.removeAttribute('aria-modal');
      this._element.removeAttribute('role');
      window.setTimeout(() => {
        this._element.classList.remove('hiding');
        this._element.setAttribute('aria-hidden', 'true');
        backdrop?.remove();
        unlockScroll(this._element);
        this._relatedTarget?.focus?.({ preventScroll: true });
        emit(this._element, 'hidden.bs.offcanvas');
      }, duration);
    }
    toggle(relatedTarget) { this._element.classList.contains('show') ? this.hide() : this.show(relatedTarget); }
  }

  class Modal extends Component {
    static NAME = 'modal';
    show(relatedTarget) {
      if (this._element.classList.contains('show')) return;
      if (!emit(this._element, 'show.bs.modal', { relatedTarget })) return;
      this._relatedTarget = relatedTarget || document.activeElement;
      this._element.style.display = 'block';
      this._element.removeAttribute('aria-hidden');
      this._element.setAttribute('aria-modal', 'true');
      this._element.setAttribute('role', 'dialog');
      this._element.classList.add('show');
      document.body.classList.add('modal-open');
      const backdrop = document.createElement('div');
      backdrop.className = 'modal-backdrop fade show';
      backdrop.dataset.v4Backdrop = this._element.id;
      backdrop.addEventListener('click', () => {
        if (this._element.dataset.bsBackdrop !== 'static') this.hide();
      });
      document.body.appendChild(backdrop);
      lockScroll(this._element);
      focusable(this._element)?.focus({ preventScroll: true });
      emit(this._element, 'shown.bs.modal', { relatedTarget });
    }
    hide() {
      if (!this._element.classList.contains('show')) return;
      if (!emit(this._element, 'hide.bs.modal')) return;
      this._element.classList.remove('show');
      this._element.style.display = 'none';
      this._element.setAttribute('aria-hidden', 'true');
      this._element.removeAttribute('aria-modal');
      this._element.removeAttribute('role');
      document.querySelector(`[data-v4-backdrop="${CSS.escape(this._element.id)}"]`)?.remove();
      unlockScroll(this._element);
      if (!document.querySelector('.modal.show')) document.body.classList.remove('modal-open');
      this._relatedTarget?.focus?.({ preventScroll: true });
      emit(this._element, 'hidden.bs.modal');
    }
    toggle(relatedTarget) { this._element.classList.contains('show') ? this.hide() : this.show(relatedTarget); }
  }

  class Collapse extends Component {
    static NAME = 'collapse';
    show() {
      if (this._element.classList.contains('show')) return;
      if (!emit(this._element, 'show.bs.collapse')) return;
      const parentSelector = this._element.dataset.bsParent;
      if (parentSelector) document.querySelectorAll(`${parentSelector} .collapse.show`).forEach((element) => {
        if (element !== this._element) Collapse.getOrCreateInstance(element).hide();
      });
      this._element.classList.add('show');
      syncExpanded(this._element, true);
      emit(this._element, 'shown.bs.collapse');
    }
    hide() {
      if (!this._element.classList.contains('show')) return;
      if (!emit(this._element, 'hide.bs.collapse')) return;
      this._element.classList.remove('show');
      syncExpanded(this._element, false);
      emit(this._element, 'hidden.bs.collapse');
    }
    toggle() { this._element.classList.contains('show') ? this.hide() : this.show(); }
  }

  const syncExpanded = (target, expanded) => {
    if (!target.id) return;
    document.querySelectorAll(`[data-bs-target="#${CSS.escape(target.id)}"], [href="#${CSS.escape(target.id)}"]`).forEach((trigger) => {
      trigger.setAttribute('aria-expanded', String(expanded));
      trigger.classList.toggle('collapsed', !expanded);
    });
  };

  class Dropdown extends Component {
    static NAME = 'dropdown';
    get menu() { return this._element.nextElementSibling?.classList.contains('dropdown-menu') ? this._element.nextElementSibling : this._element.parentElement?.querySelector('.dropdown-menu'); }
    show() {
      if (this.menu?.classList.contains('show')) return;
      document.querySelectorAll('.dropdown-menu.show').forEach((menu) => {
        const toggle = menu.previousElementSibling;
        if (toggle && toggle !== this._element) Dropdown.getOrCreateInstance(toggle).hide();
      });
      if (!emit(this._element, 'show.bs.dropdown')) return;
      this.menu?.classList.add('show');
      this._element.classList.add('show');
      this._element.setAttribute('aria-expanded', 'true');
      emit(this._element, 'shown.bs.dropdown');
    }
    hide() {
      if (!this.menu?.classList.contains('show')) return;
      if (!emit(this._element, 'hide.bs.dropdown')) return;
      this.menu.classList.remove('show');
      this._element.classList.remove('show');
      this._element.setAttribute('aria-expanded', 'false');
      emit(this._element, 'hidden.bs.dropdown');
    }
    toggle() { this.menu?.classList.contains('show') ? this.hide() : this.show(); }
  }

  class Passive extends Component {
    show() {}
    hide() {}
    toggle() {}
    update() {}
  }
  class Tooltip extends Passive { static NAME = 'tooltip'; }
  class Popover extends Passive { static NAME = 'popover'; }
  class Tab extends Passive { static NAME = 'tab'; show() {} }
  class Toast extends Passive { static NAME = 'toast'; }
  class ScrollSpy extends Passive { static NAME = 'scrollspy'; refresh() {} }

  class Carousel extends Component {
    static NAME = 'carousel';
    constructor(element, options = {}) {
      super(element);
      const configuredInterval = Number(options.interval ?? this._element.dataset.bsInterval ?? 5000);
      this._interval = Number.isFinite(configuredInterval) && configuredInterval > 0 ? configuredInterval : 5000;
      this._timer = null;
      this._touchStartX = null;
      this._element.addEventListener('mouseenter', () => this.pause());
      this._element.addEventListener('mouseleave', () => this.cycle());
      this._element.addEventListener('touchstart', (event) => {
        this._touchStartX = event.touches[0]?.clientX ?? null;
        this.pause();
      }, { passive: true });
      this._element.addEventListener('touchend', (event) => {
        const endX = event.changedTouches[0]?.clientX;
        if (this._touchStartX !== null && Number.isFinite(endX)) {
          const delta = endX - this._touchStartX;
          if (Math.abs(delta) >= 40) delta < 0 ? this.next() : this.prev();
        }
        this._touchStartX = null;
        this.cycle();
      }, { passive: true });
    }
    to(index) {
      const items = [...this._element.querySelectorAll('.carousel-item')];
      if (!items.length) return;
      const target = ((Number(index) % items.length) + items.length) % items.length;
      const current = Math.max(0, items.findIndex((item) => item.classList.contains('active')));
      items.forEach((item, itemIndex) => item.classList.toggle('active', itemIndex === target));
      this._element.querySelectorAll('.carousel-indicators [data-bs-slide-to]').forEach((indicator) => {
        const active = Number(indicator.dataset.bsSlideTo) === target;
        indicator.classList.toggle('active', active);
        if (active) indicator.setAttribute('aria-current', 'true');
        else indicator.removeAttribute('aria-current');
      });
      emit(this._element, 'slid.bs.carousel', { from: current, to: target });
    }
    next() {
      const items = [...this._element.querySelectorAll('.carousel-item')];
      const current = Math.max(0, items.findIndex((item) => item.classList.contains('active')));
      this.to((current + 1) % items.length);
    }
    prev() {
      const items = [...this._element.querySelectorAll('.carousel-item')];
      const current = Math.max(0, items.findIndex((item) => item.classList.contains('active')));
      this.to((current - 1 + items.length) % items.length);
    }
    cycle() {
      this.pause();
      if (this._element.querySelectorAll('.carousel-item').length < 2 || document.hidden) return;
      this._timer = window.setInterval(() => this.next(), this._interval);
    }
    pause() {
      if (this._timer) window.clearInterval(this._timer);
      this._timer = null;
    }
    dispose() {
      this.pause();
      super.dispose();
    }
  }

  const dismissClosest = (trigger, type) => {
    const root = trigger.closest(`.${type}`);
    if (!root) return;
    (type === 'modal' ? Modal : Offcanvas).getOrCreateInstance(root).hide();
  };

  document.addEventListener('click', (event) => {
    const carouselTrigger = event.target.closest('[data-bs-slide-to], [data-bs-slide]');
    if (carouselTrigger) {
      const carousel = targetFor(carouselTrigger);
      if (carousel) {
        event.preventDefault();
        const instance = Carousel.getOrCreateInstance(carousel);
        if (carouselTrigger.dataset.bsSlideTo !== undefined) instance.to(carouselTrigger.dataset.bsSlideTo);
        else carouselTrigger.dataset.bsSlide === 'prev' ? instance.prev() : instance.next();
        instance.cycle();
      }
      return;
    }
    const trigger = event.target.closest('[data-bs-toggle], [data-bs-dismiss]');
    if (!trigger) {
      document.querySelectorAll('.dropdown-menu.show').forEach((menu) => Dropdown.getOrCreateInstance(menu.previousElementSibling).hide());
      return;
    }
    const dismiss = trigger.dataset.bsDismiss;
    if (dismiss === 'offcanvas' || dismiss === 'modal') {
      event.preventDefault();
      dismissClosest(trigger, dismiss);
      return;
    }
    const type = trigger.dataset.bsToggle;
    if (!['offcanvas', 'modal', 'collapse', 'dropdown'].includes(type)) return;
    event.preventDefault();
    if (type === 'dropdown') Dropdown.getOrCreateInstance(trigger).toggle();
    else {
      const target = targetFor(trigger);
      if (!target) return;
      const Class = type === 'offcanvas' ? Offcanvas : type === 'modal' ? Modal : Collapse;
      Class.getOrCreateInstance(target).toggle(trigger);
    }
  });

  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;
    const modal = document.querySelector('.modal.show');
    if (modal) return Modal.getOrCreateInstance(modal).hide();
    const offcanvas = document.querySelector('.offcanvas.show');
    if (offcanvas) return Offcanvas.getOrCreateInstance(offcanvas).hide();
    document.querySelectorAll('.dropdown-menu.show').forEach((menu) => Dropdown.getOrCreateInstance(menu.previousElementSibling).hide());
  });

  window.bootstrap = {
    __v4Compat: true,
    Offcanvas,
    Modal,
    Collapse,
    Dropdown,
    Tooltip,
    Popover,
    Carousel,
    Tab,
    Toast,
    ScrollSpy,
  };

  const initializeCarousels = (root = document) => {
    const carousels = [];
    if (root instanceof Element && root.matches('[data-bs-ride="carousel"]')) carousels.push(root);
    root.querySelectorAll?.('[data-bs-ride="carousel"]').forEach((element) => carousels.push(element));
    carousels.forEach((element) => Carousel.getOrCreateInstance(element).cycle());
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initializeCarousels(), { once: true });
  else initializeCarousels();
  document.addEventListener('visibilitychange', () => {
    document.querySelectorAll('[data-bs-ride="carousel"]').forEach((element) => {
      const carousel = Carousel.getOrCreateInstance(element);
      document.hidden ? carousel.pause() : carousel.cycle();
    });
  });
  document.addEventListener('shopify:section:load', (event) => initializeCarousels(event.target));
})();
